/// <amd-module name="TFS/Admin/Groups/Common/Redux/Actions/GroupsPivotActions" />
import { ActionsUnion, createAction } from "redux/Utils/ActionHelper";
import { Avatar } from "TFS/Admin/Common/Contracts/VSS.Profile";
import { IGroupMembers, IGroupsViewData, PermissionsGroup } from "TFS/Admin/Groups/Common/Contracts";
import { GraphGroup } from "VSS/Platform/Contracts/Graph";
import { IIdentity } from "VSSUI/IdentityPicker";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { IOperationStatus } from "TFS/Admin/Common/Constants/Constants";
import { IOrganizationAadData } from "TFS/Admin/OrganizationAadCommon/Contracts/DataModels";
import { IPermissionPage, GroupOperation, SearchStatus } from "TFS/Admin/Groups/Common/Constants";

/**
 * Actions for permissions view
 */
export const GroupsPivotActions = {
    // Group pivot initializers
    initView: (isTeamsPage?: boolean) => createAction(GroupsPivotActionTypes.InitView, { isTeamsPage }),
    disposeView: () => createAction(GroupsPivotActionTypes.DisposeView),
    groupsLoadSucceeded: (pagedGroups: IGroupsViewData) => createAction(GroupsPivotActionTypes.GroupsLoadSucceeded, pagedGroups),
    updateNumberOfGroupsOnScreen: (numberOfGroupsOnScreen: number) =>
        createAction(GroupsPivotActionTypes.UpdateNumberOfGroupsOnScreen, numberOfGroupsOnScreen),
    groupsLoadFailed: () => createAction(GroupsPivotActionTypes.GroupsLoadFailed),
    selectedIdentityChanged: (identity: IIdentity) => createAction(GroupsPivotActionTypes.SelectedIdentityChanged, identity),
    fetchOrgSettings: () => createAction(GroupsPivotActionTypes.FetchOrgSettings),
    fetchOrgSettingsCompleted: (orgSettings: IOrganizationAadData) => createAction(GroupsPivotActionTypes.FetchOrgSettingsCompleted, orgSettings),
    setMarkTimeToInteract: () => createAction(GroupsPivotActionTypes.SetMarkTimeToInteract),

    // Common
    onDismissMessageCard: () => createAction(GroupsPivotActionTypes.DismissMessageCard),
    onDismissMultiSummary: () => createAction(GroupsPivotActionTypes.DismissMultiSummary),
    groupOperationStatus: (
        operation: GroupOperation,
        resultMessage: string,
        result: boolean,
        severity: MessageCardSeverity,
        group?: PermissionsGroup,
        multiOperationStatuses?: IOperationStatus[]
    ) => createAction(GroupsPivotActionTypes.GroupOperationStatus, { operation, resultMessage, result, severity, group, multiOperationStatuses }),

    // Add group actions
    onAddGroupConfirm: (
        displayName: string,
        description: string,
        avatar: Avatar,
        subjectIdentities: IIdentity[],
        adminsIdentities: IIdentity[],
        adminAsMember: boolean,
        scopeDescriptor: string | undefined,
        permissionPage: IPermissionPage,
        setAreaPath: boolean,
        supportServicePrincipals: boolean,
        parentGroupIdentity?: IIdentity
    ) =>
        createAction(GroupsPivotActionTypes.AddGroupConfirm, {
            displayName,
            description,
            avatar,
            subjectIdentities,
            adminsIdentities,
            adminAsMember,
            scopeDescriptor,
            permissionPage,
            setAreaPath,
            supportServicePrincipals,
            parentGroupIdentity
        }),
    openAddGroupPanel: (graphGroup?: PermissionsGroup) => createAction(GroupsPivotActionTypes.OpenAddGroupPanel, graphGroup),
    closeAddGroupPanel: () => createAction(GroupsPivotActionTypes.CloseAddGroupPanel),
    checkGroupExistence: (groupName: string) => createAction(GroupsPivotActionTypes.CheckGroupExistence, groupName),

    // Group action permission
    getGroupPermissions: (graphGroup: GraphGroup) => createAction(GroupsPivotActionTypes.GetGroupPermissions, graphGroup),
    fetchPermissionsGroups: () => createAction(GroupsPivotActionTypes.FetchPermissionsGroups),
    fetchPermissionsGroupsSuccess: (identities: IIdentity[]) => createAction(GroupsPivotActionTypes.FetchPermissionsGroupsSuccess, { identities }),

    // Member updates
    updateMemberCountAndLinkProps: (groupMembers: IGroupMembers, filtered: boolean) =>
        createAction(GroupsPivotActionTypes.UpdateMemberCountAndLinkProps, { groupMembers, filtered }),
    updateMembers: (groupMembers: IGroupMembers, filtered: boolean) => createAction(GroupsPivotActionTypes.UpdateMembers, { groupMembers, filtered }),

    // Search
    searchGroups: (
        groups: PermissionsGroup[],
        newKeyword?: string,
        oldKeyword?: string,
        scopeDescriptor?: string,
        permissionPage?: IPermissionPage
    ) => createAction(GroupsPivotActionTypes.SearchGroups, { groups, newKeyword, oldKeyword, scopeDescriptor, permissionPage }),
    updateSearchStatus: (searchStatus: SearchStatus) => createAction(GroupsPivotActionTypes.UpdateSearchStatus, searchStatus),
    updateSearchResult: (keyword: string, result: any[], hasMore: boolean) =>
        createAction(GroupsPivotActionTypes.UpdateSearchResult, { result, hasMore, keyword }),

    // Teams
    setDefaultTeam: (teamId: string) => createAction(GroupsPivotActionTypes.SetDefaultTeam, { teamId }),
    updateDefaultTeamFlag: (teamId: string) => createAction(GroupsPivotActionTypes.UpdateDefaultTeamFlag, { teamId }),

    // Member info
    updateMemberInfo: (groups: PermissionsGroup[], filtered: boolean, permissionPage?: IPermissionPage) =>
        createAction(GroupsPivotActionTypes.UpdateMemberInfo, { groups, filtered, permissionPage }),
    onRenderMissingItem: (groups: PermissionsGroup[], numberOfGroupsOnScreen: number, permissionPage?: IPermissionPage) =>
        createAction(GroupsPivotActionTypes.OnRenderMissingItem, { groups, numberOfGroupsOnScreen, permissionPage }),

    // Add Group Permission
    isAddGroupDisabled: (permission: boolean) => createAction(GroupsPivotActionTypes.IsAddGroupDisabled, permission),

};

export const enum GroupsPivotActionTypes {
    InitView = "GroupsView/InitView",
    DisposeView = "GroupsView/Dispose",
    GroupsLoadSucceeded = "GroupsView/GroupsLoadSucceeded",
    GroupsLoadFailed = "GroupsView/GroupsLoadFailed",
    FetchMoreGroups = "GroupsView/FetchMoreGroups",
    FetchMoreGroupsSucceeded = "GroupsView/FetchMoreGroupsSucceeded",
    SelectedIdentityChanged = "GroupsView/GroupsPivot/SelectedIdentityChanged",
    FetchOrgSettings = "GroupsView/FetchOrgSettings",
    FetchOrgSettingsCompleted = "GroupsView/FetchIsOrgSettingsCompleted",
    SetMarkTimeToInteract = "GroupsView/SetMarkTimeToInteract",
    UpdateMemberInfo = "GroupsView/UpdateMemberInfo",
    OnRenderMissingItem = "GroupsView/OnRenderMissingItem",
    UpdateNumberOfGroupsOnScreen = "GroupsView/UpdateNumberOfGroupsOnScreen",

    // Common
    DismissMessageCard = "GroupsView/DismissMessageCard",
    GroupOperationStatus = "GroupsView/GroupOperationStatus",
    DismissMultiSummary = "GroupsView/DismissMultiSummary",

    // Add
    AddGroupConfirm = "GroupsView/AddGroupConfirm",
    OpenAddGroupPanel = "GroupsView/OpenAddGroupPanel",
    CloseAddGroupPanel = "GroupsView/CloseAddGroupPanel",
    CheckGroupExistence = "GroupsView/CheckGroupExistence",

    // Group action permissions
    GetGroupPermissions = "GroupsView/GetGroupPermissions",
    IsAddGroupDisabled = "GroupsView/IsAddGroupDisabled",

    // Member updates
    UpdateMemberCountAndLinkProps = "GroupsView/UpdateMemberCountAndLinkProps",
    UpdateMembers = "GroupsView/UpdateMembers",

    // Search
    SearchGroups = "GroupsView/SearchGroups",
    UpdateSearchStatus = "GroupsView/UpdateSearchStatus",
    UpdateSearchResult = "GroupsView/UpdateSearchResult",

    // SetDefaultTeam
    SetDefaultTeam = "GroupsView/SetDefaultTeam",
    UpdateDefaultTeamFlag = "GroupsView/UpdateDefaultTeamFlag",

    // AddTeams
    FetchPermissionsGroups = "GroupsView/FetchPermissionsGroups",
    FetchPermissionsGroupsSuccess = "GroupsView/FetchPermissionsGroupsSuccess"
}

export type GroupsPivotActions = ActionsUnion<typeof GroupsPivotActions>;
